/**
 *   LookupType determines how the UI should interact with this lookup property. Depending on the type of lookup, the UI
*   will need to obtain values in different ways.
 */
export enum LookupType {
    OPTIONS = 'options',
    SINGLE = 'single',
    DEFINITION = 'definition'
}


/**
 *  PropertyValidation determines special validation rules over the primitive data type of a property.
 */
export enum PropertyValidation {
    URL = 'url',
    INT = 'int',
    FLOAT = 'float',
    EMAIL = 'email',
    DATE = 'date',
    DATETIME = 'datetime'
}

/**
 *  PropertyType determines the data type of a workflow action input property.
 */
export enum PropertyType {
    STRING = 'string',
    INT = 'int',
    FLOAT = 'float',
    RECORD = 'record',
    DATE = 'date',
    DATETIME = 'datetime',
    BOOL = 'bool'
}

/**
* The response from the workflow-actions/:workflow_action_eid/properties/:property_name/values/* endpoints
*/
export interface ActionPropertyValuesResult {
    // this value is what is shown to the user
    display_value: string;
    // this is what is stored in the workflow
    value: any;
}

/**
*   SingleLookup is used when the lookup type is LookupTypeSingle. It contains the type and displayName of the
*   lookup input the UI has to render for the user to paste an identifier into. This is used in Marketo actions,
*   for example, to provide list or campaign urls.
*/
interface SingleLookup {
    display_name: string;
    type: PropertyType;
    validation: PropertyValidation;
}

/**
*   OptionsLookup is used when the lookup type is LookupTypeOptions.
*   It only specifies whether the lookup supports searching right now, but it can be extended.
*/
interface OptionsLookup {
    supports_search: boolean;
}

/**
*   Lookup is used to tell the UI that a property's values should be obtained by subsequent API requests.
*   They may provide search capabilities and have different types.
*/
export interface Lookup {
    type: LookupType;
    single_lookup?: SingleLookup;
    options_lookup?: OptionsLookup;
}

/**
*   RecordField represents a field in a property of type "record". These work as sub-fields of the workflow action
*   configuration.
*/
export interface RecordField {
    name: string;
    display_name: string;
    type: PropertyType;
    multivalued: boolean;
    validation?: PropertyValidation;
    required: boolean;
    createable: boolean;
    updateable: boolean;
    depends_on?: string[] | null;
    enum?: ActionPropertyValuesResult[];
    lookup?: Lookup;
    missing?: boolean; // UI only prop to signal if field is missing
}
